import { useContext, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import ContactSection from "../components/contactSection.jsx";
import ContentText from "../components/contentText.jsx";
import {
  breadcrumbJsonLd,
  CategoryTiles,
  itemListJsonLd,
  MachineGrid,
} from "../components/machineCollection.jsx";
import { manufacturerLogo } from "../components/manufacturerLogos.js";
import PageHero from "../components/pageHero.jsx";
import SectionContent from "../components/sectionContent.jsx";
import { MachineContext } from "../context/MachineContext";
import { SiteContentContext } from "../context/SiteContentContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { absoluteUrl, metaDescription as toMetaDescription } from "../seoUtils";
import { machineCategoryUrlSlug, slugify } from "../slug.js";

/**
 * /{locale}/manufacturers/:slug — one page per brand in the catalogue.
 *
 * Manufacturers are not an entity in the database, only a string on each machine, so the page
 * is resolved by slugifying that string. The intro copy comes from the site content key
 * `manufacturer.<slug>.description`; without one, the fallback is built from the data itself
 * rather than from claims about the brand we cannot check.
 */

export function manufacturerTitle(name) {
  return `${name}-maskiner – återförsäljare i Sverige | AS Maskiner`;
}

/** Fallback intro: only what the catalogue itself proves. */
export function fallbackDescription(name, categoryNames) {
  const stock = categoryNames.length ? ` I lager just nu: ${categoryNames.join(", ")}.` : "";
  return (
    `AS Maskiner säljer och servar ${name}-maskiner i Sverige och Norden.` +
    `${stock} Vi hjälper till med leverans, installation, service och finansiering.`
  );
}

export default function ManufacturerPage() {
  const { slug } = useParams();
  const { machines } = useContext(MachineContext);
  const { getContent } = useContext(SiteContentContext);
  const { locale, localePath } = useLocalePath();

  const manufacturerMachines = useMemo(
    () => machines.filter((machine) => slugify(machine.manufacturer) === slug),
    [machines, slug]
  );

  const name = manufacturerMachines[0]?.manufacturer || "";

  // Every category this brand actually has machines in — the intro text and the links below it
  // are derived from the catalogue, so they can never claim more than we have.
  const categories = useMemo(() => {
    const seen = new Map();
    for (const machine of manufacturerMachines) {
      if (machine.category && !seen.has(machine.category)) {
        seen.set(machine.category, machineCategoryUrlSlug(machine));
      }
    }
    return [...seen].map(([categoryName, categorySlug]) => ({ categoryName, categorySlug }));
  }, [manufacturerMachines]);

  if (!name) {
    return machines.length > 0 ? <ManufacturerNotFound /> : null;
  }

  const path = `/manufacturers/${slug}`;
  const absolute = (target) => absoluteUrl(locale, target);
  const url = absolute(path);
  const title = manufacturerTitle(name);
  const description =
    getContent(`manufacturer.${slug}.description`) ||
    fallbackDescription(
      name,
      categories.map((category) => category.categoryName)
    );
  const metaDescription = toMetaDescription(description);
  const logo = manufacturerLogo(name);
  const itemListElement = itemListJsonLd(absolute, manufacturerMachines);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            url,
            name: `${name} – maskiner hos AS Maskiner`,
            description,
            about: { "@type": "Brand", name },
            ...(itemListElement.length > 0
              ? {
                  mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: manufacturerMachines.length,
                    itemListElement,
                  },
                }
              : {}),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(absolute, { name, path }))}
        </script>
      </Helmet>

      {/* The same header every other page uses: one admin-managed background video over a
          fallback image. The brand's intro sits in it as the subtitle, still through
          ContentText so the "Visa nycklar" overlay can edit it in place. */}
      <PageHero
        title={`${name}-maskiner`}
        subtitle={
          <ContentText contentKey={`manufacturer.${slug}.description`} fallback={description} />
        }
        backgroundKey="catalogue.manufacturer_hero_background"
        alignment="bottom"
        // These two keys ship empty and stay that way until an admin uploads something, so the
        // fallback is the site's blue rather than PageHero's light default — white title text
        // on near-white is unreadable.
        fallbackClassName="bg-primary"
      />

      {(logo || categories.length > 0) && (
        <section className="bg-surface py-8 md:py-12">
          <SectionContent>
            {/* The wordmarks are dark, so they sit here on white rather than over the hero. */}
            {logo && (
              <img
                src={logo}
                alt={`${name} logotyp`}
                className="h-10 object-contain object-left"
                data-testid="manufacturer-logo"
              />
            )}
            {categories.length > 0 && (
              <div className={`flex flex-wrap gap-2 ${logo ? "mt-6" : ""}`}>
                {categories.map((category) => (
                  <Link
                    key={category.categorySlug}
                    to={localePath(`/catalogue/${category.categorySlug}`)}
                    data-testid={`brand-category-${category.categorySlug}`}
                    className="inline-flex min-h-11 items-center rounded-full border border-border-subtle bg-surface-alt px-4 text-sm font-medium text-text-strong hover:border-primary hover:text-primary transition-colors"
                  >
                    {category.categoryName}
                  </Link>
                ))}
              </div>
            )}
          </SectionContent>
        </section>
      )}

      <MachineGrid machines={manufacturerMachines} emptyFor={`${name}-maskiner`} />

      <CategoryTiles headingKey="landing.brand_categories_heading" headingFallback="Kategorier" />

      <ContactSection />
    </>
  );
}

function ManufacturerNotFound() {
  const { localePath } = useLocalePath();

  return (
    <div className="flex flex-col items-center justify-center h-[75vh] p-4 max-w-2xl mx-auto">
      <Helmet>
        <title>Fabrikatet finns inte | AS Maskiner</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="shadow-lg p-8 w-full text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary">
          <ContentText
            contentKey="landing.manufacturer_not_found"
            fallback="Det här fabrikatet finns inte i katalogen"
          />
        </h1>
        <p className="mb-8 text-text-body">
          <ContentText
            contentKey="landing.manufacturer_not_found_body"
            fallback="Vi har inga maskiner från det här fabrikatet just nu. Titta i katalogen så hittar du allt vi har i lager."
          />
        </p>
        <Link
          to={localePath("/catalogue")}
          className="inline-block bg-primary text-inverse py-2 px-6 rounded-md shadow-md hover:bg-primary800 transition-colors duration-300"
        >
          <ContentText contentKey="landing.not_found_cta" fallback="Till maskinkatalogen" />
        </Link>
      </div>
    </div>
  );
}
